import { useCallback, useEffect, useState } from 'react';
import type { ValueOf } from '@direct-frontend/stdlib';
import { getWindow } from '../../../lib/getWindow';
import { loadVideoPlayer } from './loadVideoPlayer';
import type { PlayerInitParams, VideoPlayerInstance, YandexGlobal, YandexPlayerApi } from './types';

/**
 * Статусы загрузки видеоплеера
 */
export const VideoPlayerLoadStatus = {
    /** Плеер не инициализирован */
    NOT_INITIALIZED: 'NOT_INITIALIZED',
    /** Загрузка скрипта плеера */
    LOADING: 'LOADING',
    /** Плеер готов к использованию */
    READY: 'READY',
    /** Ошибка загрузки плеера */
    ERROR: 'ERROR',
} as const;

export type VideoPlayerLoadStatusType = ValueOf<typeof VideoPlayerLoadStatus>;

/**
 * Параметры для загрузки видеоплеера
 */
export interface VideoPlayerLoadOptions {
    /** CSP nonce для скрипта */
    cspNonce?: string;
    /** Версия плеера */
    version?: string;
}

/**
 * Результат хука useVideoPlayer
 */
export interface UseVideoPlayerResult {
    /** Текущий статус загрузки плеера */
    status: VideoPlayerLoadStatusType;
    /** API фабрики плеера Яндекса (доступно только когда status === 'READY') */
    playerApi: YandexPlayerApi | null;
    /** Функция для создания экземпляра плеера */
    createPlayer: ((params: PlayerInitParams) => VideoPlayerInstance) | null;
    /** Ошибка загрузки (если есть) */
    error: Error | null;
    /** Функция для повторной попытки загрузки */
    retry: () => void;
}

export function useVideoPlayer(options?: VideoPlayerLoadOptions): UseVideoPlayerResult {
    const [status, setStatus] = useState<VideoPlayerLoadStatusType>(VideoPlayerLoadStatus.NOT_INITIALIZED);
    const [error, setError] = useState<Error | null>(null);

    const loadPlayer = useCallback(async () => {
        try {
            setStatus(VideoPlayerLoadStatus.LOADING);
            setError(null);

            await loadVideoPlayer(options);

            const window = getWindow();
            const Ya = (window as typeof window & { Ya?: YandexGlobal })?.Ya;

            if (!Ya?.playerApi) {
                throw new Error('Yandex Player API не найден после загрузки скрипта');
            }

            setStatus(VideoPlayerLoadStatus.READY);
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));

            setError(error);
            setStatus(VideoPlayerLoadStatus.ERROR);
        }
    }, [options]);

    const retry = useCallback(() => {
        loadPlayer();
    }, [loadPlayer]);

    useEffect(() => {
        const window = getWindow();
        const Ya = (window as typeof window & { Ya?: YandexGlobal })?.Ya;

        if (Ya?.playerApi) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- Автоматический игнор в рамках DIRECT-312943
            setStatus(VideoPlayerLoadStatus.READY);

            return;
        }

        loadPlayer();
    }, [loadPlayer]);

    const window = getWindow();
    const Ya = (window as typeof window & { Ya?: YandexGlobal })?.Ya;

    const playerApi = status === VideoPlayerLoadStatus.READY && Ya?.playerApi ? Ya.playerApi : null;
    const createPlayer = playerApi ? (params: PlayerInitParams) => playerApi.create(params) : null;

    return {
        status,
        playerApi,
        createPlayer,
        error,
        retry,
    };
}
